import { LightningElement, api } from "lwc";
import { LabelTranslations } from "tbme/localization";
import { t } from "tbme/localization";
import { validateWebsite, validateSocialHandle } from "../profileEdit/form";
import { requestLeaveSite } from "../leaveSiteModal/requestLeaveSite";
import {
  sendProfileAnalyticsEvent,
  AnalyticEventName,
  AnalyticsPageElement,
  AnalyticsLinkElementType,
} from "../../../shared/analyticsProfile";
interface SocialLink {
  name: string;
  href: string;
  class: string;
  assistiveText: string;
}

/** Link type used for "My Website" - only this shows the leave-site modal. */
const WEBSITE_LINK_TYPE = "link";

export default class extends LightningElement {
  _twitterHandle?: SocialLink;
  _linkedinHandle?: SocialLink;
  _facebookHandle?: SocialLink;
  _websiteUrl?: SocialLink;

  private labels: LabelTranslations = undefined!;

  @api name?: string;

  @api
  get twitterHandle(): SocialLink | undefined | string {
    return this._twitterHandle;
  }
  set twitterHandle(value: SocialLink | undefined | string) {
    value = validateSocialHandle("https://www.x.com", value as string)
      ? value
      : undefined;
    this._twitterHandle = value
      ? ({
          name: "x",
          href: `https://www.x.com/${value}`,
          class: "social twitter",
          assistiveText: t("socialLinks.twitterAssistiveText", {
            name: this.name,
          }),
        } as SocialLink)
      : undefined;
  }

  @api
  get linkedinHandle(): SocialLink | undefined | string {
    return this._linkedinHandle;
  }
  set linkedinHandle(value: SocialLink | undefined | string) {
    value = validateSocialHandle("https://www.linkedin.com/in", value as string)
      ? value
      : undefined;
    this._linkedinHandle = value
      ? ({
          name: "linkedin",
          href: `https://www.linkedin.com/in/${value}`,
          class: "social linkedin",
          assistiveText: t("socialLinks.linkedInAssistiveText", {
            name: this.name,
          }),
        } as SocialLink)
      : undefined;
  }

  @api
  get facebookHandle(): SocialLink | undefined | string {
    return this._facebookHandle;
  }
  set facebookHandle(value: SocialLink | undefined | string) {
    value = validateSocialHandle("https://www.facebook.com", value as string)
      ? value
      : undefined;
    this._facebookHandle = value
      ? ({
          name: "facebook",
          href: `https://www.facebook.com/${value}`,
          class: "social facebook",
          assistiveText: t("socialLinks.facebookAssistiveText", {
            name: this.name,
          }),
        } as SocialLink)
      : undefined;
  }

  @api
  get websiteUrl(): SocialLink | undefined | string {
    return this._websiteUrl;
  }
  set websiteUrl(value: SocialLink | undefined | string) {
    let website = validateWebsite(value as string);
    this._websiteUrl = website
      ? ({
          name: "link",
          href: `${website}`,
          class: "social link",
          assistiveText: t("socialLinks.websiteAssistiveText", {
            name: this.name,
          }),
        } as SocialLink)
      : undefined;
  }

  get socialLinks() {
    return [
      this.linkedinHandle,
      this.twitterHandle,
      this.facebookHandle,
      this.websiteUrl,
    ].filter((link) => link);
  }

  get isVisible() {
    return this.socialLinks.length > 0;
  }

  private analyticsLabelKeyForLinkType(linkType: string): string | undefined {
    switch (linkType) {
      case "x":
        return "profileEditModal.fieldTwitter";
      case "linkedin":
        return "profileEditModal.fieldLinkedIn";
      case "facebook":
        return "profileEditModal.fieldFacebook";
      case "link":
        return "profileEditModal.fieldWebsite";
      default:
        return undefined;
    }
  }

  private dispatchSocialLinkAnalyticsEvent(linkType: string | null, href: string) {
    const labelKey = linkType && this.analyticsLabelKeyForLinkType(linkType);
    if (!labelKey) {
      return;
    }
    sendProfileAnalyticsEvent(
      AnalyticEventName.CONTENT_CLICK,
      AnalyticsPageElement.SOCIAL_LINK,
      labelKey,
      AnalyticsLinkElementType.LINK,
      href
    );
  }

  handleSocialLinkClick(evt: Event) {
    const target = evt.currentTarget as HTMLElement;
    const href = target?.getAttribute("data-href");
    const linkType = target?.getAttribute("data-link-type");

    if (!href) return;

    this.dispatchSocialLinkAnalyticsEvent(linkType, href);

    // Only intercept website links to show the leave-site modal
    // For other social links, let the browser handle the default behavior
    if (linkType === WEBSITE_LINK_TYPE) {
      evt.preventDefault();
      requestLeaveSite(href);
    }
  }
}
